import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Card, CardContent } from '../../components/ui/card'
import { Wizard, WizardActions } from '../../components/Wizard'
import { PortSelector } from '../../components/PortSelector'
import { PhoneInput } from '../../components/PhoneInput'
import { QuoteSummary } from '../../components/QuoteSummary'
import { NCMAutocomplete } from '../../components/NCMAutocomplete'
import { Dialog, DialogHeader, DialogContent, DialogFooter } from '../../components/ui/Dialog'
import { AddressAutocomplete } from '../../components/AddressAutocomplete'
import {
  ArrowLeft,
  Sparkle,
  Plus,
  Trash,
  Boat,
  Airplane,
  Truck,
  Package,
  MapPin,
  Cube,
  FileText,
  UsersThree,
  Warning,
  ArrowRight,
  CalendarBlank,
  CheckCircle,
  WarningCircle,
  X
} from '@phosphor-icons/react'
import api from '../../services/api'
import { createQuote, getModalities, getQuote, updateQuote, activateQuote } from '../../services/quotes'
import { CreateQuoteData, Modality, INCOTERM_LABELS, CARGO_TYPE_LABELS, Quote } from '../../types/quote'
import { DocumentAttachments } from '../../components/DocumentAttachments'
import { uploadQuoteDocument, type AttachedDocument } from '../../services/quoteDocuments'
import { getShipperAgents } from '../../services/shipper-agents'
import { useDialog } from '../../context/DialogContext'
import { useAuth } from '../../context/AuthContext'
import { formatDateBR } from '../../lib/date'

export default function NewQuotePageWizard() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { showSuccess, showError } = useDialog()
  const { user } = useAuth()
  const isEditMode = Boolean(id)
  // A shipper pending Eximtech approval can browse but not create/send quotes.
  const shipperPending = user?.current_company?.type === 'shipper' && user?.current_company?.is_approved === false

  const [currentStep, setCurrentStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [modalities, setModalities] = useState<Modality[]>([])
  const [loadingModalities, setLoadingModalities] = useState(true)
  const [loadingQuote, setLoadingQuote] = useState(isEditMode)
  const [existingQuote, setExistingQuote] = useState<Quote | null>(null)
  const [hasAgents, setHasAgents] = useState<boolean | null>(null)
  const [loadingAgents, setLoadingAgents] = useState(true)

  // Multi-NCM state
  const [ncmList, setNcmList] = useState<string[]>([])
  const [extracting, setExtracting] = useState(false)
  const extractInputRef = useRef<HTMLInputElement>(null)

  // BETA: upload a document (invoice/packing list), let AI extract the fields
  // and pre-fill the wizard. The user reviews/edits everything before saving.
  async function handleExtractDocument(file: File) {
    setExtracting(true)
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await api.post('/shipper/quotes/extract-document', form)
      const { extracted, modalityId } = res.data as { extracted: any; modalityId: string | null }
      // Don't touch the form yet — show a review modal first so the user
      // confirms/sees what was captured before we pre-fill the wizard.
      setReviewData({ extracted, modalityId })
    } catch (err: any) {
      showError('Erro na análise', err.response?.data?.error || 'Não foi possível ler o documento')
    } finally {
      setExtracting(false)
      if (extractInputRef.current) extractInputRef.current.value = ''
    }
  }

  // Apply the reviewed extraction into the wizard fields, then jump to the
  // modality details step so the user continues filling what's missing.
  function applyExtracted(extracted: any, modalityId: string | null) {
    if (extracted.modality) setSelectedModal(extracted.modality)
    if (extracted.operation) setSelectedType(extracted.operation)
    if (extracted.modality === 'maritimo' && extracted.volumeType) {
      const vt = String(extracted.volumeType).toLowerCase()
      setSelectedVolume(/container|20|40|gp|hc|fcl/.test(vt) ? 'FCL' : 'LCL')
    }
    if (modalityId) setValue('modality_id', modalityId)
    if (extracted.modality) setModalitySubStep('details')

    if (extracted.incoterm) setValue('incoterm', String(extracted.incoterm).toUpperCase() as any)
    if (extracted.goodsDescription) setValue('goods_description', extracted.goodsDescription)
    if (extracted.ncm) {
      const codes = [String(extracted.ncm).replace(/[^\d.]/g, '')]
      setNcmList(codes)
      setValue('goods_ncm_list', codes)
      setValue('goods_ncm', codes[0])
    }
    if (extracted.clientReference) setValue('client_reference', extracted.clientReference)

    if (extracted.originCity || extracted.originCountry) {
      setValue('origin.location.city', extracted.originCity || '')
      if (extracted.originCountry) setValue('origin.location.country', extracted.originCountry)
    }
    if (extracted.destinationCity || extracted.destinationCountry) {
      setValue('destination.location.city', extracted.destinationCity || '')
      if (extracted.destinationCountry) setValue('destination.location.country', extracted.destinationCountry)
    }

    if (extracted.volumesCount || extracted.grossWeightKg) {
      setValue('volume_items', [{
        type: extracted.volumeType || 'Volume',
        quantity: extracted.volumesCount || 1,
        weight: extracted.grossWeightKg ? Math.round((extracted.grossWeightKg / (extracted.volumesCount || 1)) * 100) / 100 : 0,
      }] as any)
    }

    setReviewData(null)
    showSuccess('Dados aplicados', 'Preenchemos o que foi possível. Revise e complete o que faltou antes de enviar.')
  }

  // Record which fields the extraction missed so we can improve the prompt /
  // build an eval set later. Fire-and-forget — never blocks the user.
  function logExtractionFeedback(extracted: any, missingLabels: string[]) {
    api.post('/shipper/quotes/extract-feedback', {
      missingFields: missingLabels,
      extracted,
    }).catch(() => {})
  }
  const [attachedDocs, setAttachedDocs] = useState<AttachedDocument[]>([])
  const [reviewData, setReviewData] = useState<{ extracted: any; modalityId: string | null } | null>(null)

  // Sub-step states for modality selection (2-step flow)
  const [modalitySubStep, setModalitySubStep] = useState<'modal' | 'details'>('modal')
  const [selectedModal, setSelectedModal] = useState<'maritimo' | 'aereo' | 'terrestre' | null>(null)
  const [selectedType, setSelectedType] = useState<'import' | 'export' | null>(null)
  const [selectedVolume, setSelectedVolume] = useState<'FCL' | 'LCL' | null>(null)

  const { register, handleSubmit, watch, control, setValue, formState: { errors } } = useForm<CreateQuoteData>({
    defaultValues: {
      goods_special: false,
      request_platform_agents: true,
      request_insurance_quote: true,
      goods_value_currency: 'USD',
      volume_items: [
        {
          type: 'Pallet',
          quantity: 1,
          weight: 0,
          dimensions: {
            length: 0,
            width: 0,
            height: 0,
            unit: 'cm'
          }
        }
      ]
    }
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'volume_items'
  })

  const selectedModality = watch('modality_id')
  const selectedIncoterm = watch('incoterm')

  // Get selected modality details
  const currentModality = modalities.find(m => m.id === selectedModality)

  // Determine location type for origin based on modality and incoterm
  // According to incoterm rules:
  // - FOB/FAS/CFR: Port of origin + Port of destination
  // - CIF/CPT/CIP/DAP/DPU/DDP: Collection location at origin + Delivery location at destination
  // - EXW/FCA: Collection location at origin + Port of destination
  const getOriginLocationType = (): 'seaport' | 'airport' | undefined => {
    // Incoterms that require port/airport at origin
    const portAtOriginIncoterms = ['FOB', 'FAS', 'CFR']

    if (selectedIncoterm && portAtOriginIncoterms.includes(selectedIncoterm)) {
      if (currentModality?.name === 'maritimo') return 'seaport'
      if (currentModality?.name === 'aereo') return 'airport'
    }

    // For CIF, EXW, FCA, CPT, CIP, DAP, DPU, DDP - origin is a collection/general location
    return undefined
  }

  // Determine location type for destination based on modality and incoterm
  const getDestinationLocationType = (): 'seaport' | 'airport' | undefined => {
    // Incoterms that require delivery location at destination (not port)
    const locationAtDestIncoterms = ['CIF', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP']

    if (selectedIncoterm && locationAtDestIncoterms.includes(selectedIncoterm)) {
      // Destination is a delivery location, not a port
      return undefined
    }

    // For FOB, FAS, CFR, EXW, FCA - destination is a port
    if (currentModality?.name === 'maritimo') return 'seaport'
    if (currentModality?.name === 'aereo') return 'airport'
    return undefined
  }

  // No incoterm currently needs a separate pickup_location section.
  // Origin + destination are sufficient for every supported incoterm.
  const needsThirdLocation = (): { needed: boolean; label: string; description: string } => {
    return { needed: false, label: '', description: '' }
  }

  // Find modality ID based on selected modal, type, and volume
  const findModalityId = () => {
    if (!selectedModal || !selectedType) return null

    return modalities.find(m =>
      m.name === selectedModal &&
      m.type === selectedType &&
      (selectedModal !== 'maritimo' || m.volume_name === selectedVolume)
    )?.id || null
  }

  // Sync modality selection with form
  useEffect(() => {
    const modalityId = findModalityId()
    if (modalityId) {
      setValue('modality_id', modalityId)
    }
  }, [selectedModal, selectedType, selectedVolume, modalities])

  useEffect(() => {
    loadModalities()
    checkAgents()
  }, [])

  async function checkAgents() {
    try {
      setLoadingAgents(true)
      const agents = await getShipperAgents()
      setHasAgents(agents.length > 0)
    } catch (error) {
      console.error('Erro ao verificar agentes:', error)
      setHasAgents(false)
    } finally {
      setLoadingAgents(false)
    }
  }

  useEffect(() => {
    if (isEditMode && id) {
      loadQuoteData(id)
    }
  }, [isEditMode, id])

  async function loadModalities() {
    try {
      const data = await getModalities()
      setModalities(data.filter(m => m.is_active))
    } catch (error) {
      console.error('Erro ao carregar modalidades:', error)
    } finally {
      setLoadingModalities(false)
    }
  }

  async function loadQuoteData(quoteId: string) {
    try {
      setLoadingQuote(true)
      const quote = await getQuote(quoteId)
      setExistingQuote(quote)

      // Populate form with existing data
      setValue('modality_id', quote.modality_id)
      setValue('incoterm', quote.incoterm)
      setValue('origin', quote.origin)
      setValue('destination', quote.destination)
      if (quote.pickup_location) setValue('pickup_location', quote.pickup_location)
      setValue('goods_description', quote.goods_description)
      // Populate multi-NCM: prefer goods_ncm_list, fallback to single goods_ncm
      const loadedNcmList = (quote as any).goods_ncm_list && (quote as any).goods_ncm_list.length > 0
        ? (quote as any).goods_ncm_list
        : ((quote as any).goods_ncm ? [(quote as any).goods_ncm] : [])
      setNcmList(loadedNcmList)
      setValue('goods_ncm_list', loadedNcmList)
      setValue('goods_ncm', loadedNcmList[0] || '')
      setValue('goods_special', quote.goods_special || false)
      setValue('goods_type', quote.goods_type)
      if ((quote as any).goods_value != null) setValue('goods_value', Number((quote as any).goods_value))
      if ((quote as any).goods_value_currency) setValue('goods_value_currency', (quote as any).goods_value_currency)
      if (quote.client_reference) setValue('client_reference', quote.client_reference)
      if (quote.notes) setValue('notes', quote.notes)
      if (quote.volume_items) setValue('volume_items', quote.volume_items)
      if (quote.documents && quote.documents.length > 0) {
        setAttachedDocs(
          quote.documents.map((d) => ({
            name: d.name,
            url: d.url,
            key: d.key,
            mimeType: d.mimeType || d.type || '',
            size: d.size,
            uploaded_at: d.uploaded_at,
            uploaded_by_name: d.uploaded_by_name,
          }))
        )
      }
    } catch (error) {
      console.error('Erro ao carregar cotação:', error)
      showError('Erro', 'Erro ao carregar cotação')
      navigate('/shipper/quotes')
    } finally {
      setLoadingQuote(false)
    }
  }

  // When editing, sync sub-step states with existing quote modality
  useEffect(() => {
    if (existingQuote && modalities.length > 0) {
      const mod = modalities.find(m => m.id === existingQuote.modality_id)
      if (mod) {
        setSelectedModal(mod.name as 'maritimo' | 'aereo' | 'terrestre')
        setSelectedType(mod.type as 'import' | 'export')
        if (mod.volume_name) {
          setSelectedVolume(mod.volume_name as 'FCL' | 'LCL')
        }
        setModalitySubStep('details')
      }
    }
  }, [existingQuote, modalities])

  // activate=true (default) envia aos agentes; activate=false salva como rascunho
  async function onSubmit(data: CreateQuoteData, activate: boolean = true) {
    try {
      setLoading(true)

      // Attach multi-NCM data
      data.goods_ncm_list = ncmList
      data.goods_ncm = ncmList.length > 0 ? ncmList[0] : undefined

      // Attach uploaded documents
      data.documents = attachedDocs

      // pickup_location was previously collected for EXW; drop any stale
      // value so we never persist it now that the field has been removed.
      delete (data as any).pickup_location

      if (isEditMode && id) {
        await updateQuote(id, data)
        showSuccess('Cotação Atualizada', 'A cotação foi atualizada com sucesso!')
      } else {
        // Criar a cotação (nasce como rascunho)
        const quote = await createQuote(data)

        if (activate) {
          // Ativar imediatamente para enviar aos agentes
          await activateQuote(quote.id)
          showSuccess('Cotação Enviada', 'A cotação foi enviada para os agentes!')
        } else {
          showSuccess('Rascunho Salvo', 'A cotação foi salva como rascunho. Você pode enviá-la aos agentes depois.')
        }
      }

      navigate('/shipper/quotes')
    } catch (error: any) {
      console.error('Erro ao salvar cotação:', error)
      showError('Erro', error.response?.data?.error || 'Erro ao salvar cotação')
    } finally {
      setLoading(false)
    }
  }

  const modalityIcons = {
    maritimo: <Boat size={24} weight="duotone" />,
    aereo: <Airplane size={24} weight="duotone" />,
    terrestre: <Truck size={24} weight="duotone" />
  }

  const steps = [
    { id: 'modality', title: 'Modalidade', description: 'Tipo de transporte', icon: <Package /> },
    { id: 'route', title: 'Rota', description: 'Origem e destino', icon: <MapPin /> },
    { id: 'cargo', title: 'Carga', description: 'Detalhes dos volumes', icon: <Cube /> },
    { id: 'review', title: 'Revisão', description: 'Confirmar dados', icon: <FileText /> }
  ]

  const scrollToTop = () => {
    // The actual scroll happens in the effect below; this exists so existing
    // callers (handleNext/handleBack and sub-step toggles) still compile.
  }

  // Scroll the page back to the top whenever the step or sub-step changes.
  // Doing this in an effect (instead of inline after setState) guarantees
  // the scroll happens *after* React commits and the new step has painted,
  // avoiding the smooth-scroll being cancelled by the layout shift between
  // long and short steps.
  useEffect(() => {
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'auto' })
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    })
  }, [currentStep, modalitySubStep])

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
      scrollToTop()
    }
  }

  const handleBack = () => {
    // Handle sub-step navigation within modality step
    if (currentStep === 0 && modalitySubStep === 'details') {
      setModalitySubStep('modal')
      scrollToTop()
      return
    }
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
      scrollToTop()
    }
  }

  const canProgress = () => {
    switch (currentStep) {
      case 0: // Modality step
        // Validate sub-step completion
        if (modalitySubStep === 'modal') return false
        if (!selectedModal || !selectedType) return false
        if (selectedModal === 'maritimo' && !selectedVolume) return false
        return !!selectedModality && !!selectedIncoterm
      case 1: // Route step
        return watch('origin.location.city') && watch('destination.location.city')
      case 2: // Cargo step
        return watch('volume_items')?.length > 0
      default:
        return true
    }
  }

  if (loadingModalities) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-screen">
          <p className="text-gray-500">Carregando...</p>
        </div>
      </AppLayout>
    )
  }

  if (loadingQuote || loadingModalities || loadingAgents) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-screen">
          <p className="text-gray-500">Carregando...</p>
        </div>
      </AppLayout>
    )
  }

  // Check if shipper has agents before allowing quote creation
  if (shipperPending) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center p-4">
          <Card className="max-w-lg w-full">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
                <Warning size={40} className="text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Cadastro em análise
              </h2>
              <p className="text-gray-600 mb-6">
                Seu cadastro está sendo revisado pela equipe Eximtech. Assim que for liberado,
                você poderá criar e enviar cotações. Enquanto isso, você já pode completar seu
                perfil e cadastrar seus agentes de carga.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button variant="outline" onClick={() => navigate('/shipper/quotes')} className="gap-2">
                  <ArrowLeft size={20} />
                  Voltar
                </Button>
                <Link to="/shipper/agents">
                  <Button className="gap-2 w-full sm:w-auto" style={{ backgroundColor: 'rgb(5, 150, 105)' }}>
                    <UsersThree size={20} />
                    Gerenciar Agentes
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    )
  }

  if (!isEditMode && hasAgents === false) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center p-4">
          <Card className="max-w-lg w-full">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
                <Warning size={40} className="text-amber-600" />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Cadastre seus Agentes de Carga
              </h2>

              <p className="text-gray-600 mb-6">
                Para criar cotacoes, voce precisa primeiro ter agentes de carga cadastrados.
                Os agentes sao os parceiros que irao receber suas solicitacoes de frete e enviar propostas.
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <UsersThree size={24} className="text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-left">
                    <p className="text-sm text-blue-800 font-medium">Como funciona?</p>
                    <p className="text-sm text-blue-700 mt-1">
                      1. Acesse o menu "Meus Agentes"<br />
                      2. Convide agentes de carga existentes ou por email<br />
                      3. Aguarde a confirmacao do agente<br />
                      4. Pronto! Voce ja pode criar cotacoes
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={() => navigate('/shipper/quotes')}
                  className="gap-2"
                >
                  <ArrowLeft size={20} />
                  Voltar
                </Button>
                <Link to="/shipper/agents">
                  <Button
                    className="gap-2 w-full sm:w-auto"
                    style={{ backgroundColor: 'rgb(5, 150, 105)' }}
                  >
                    <UsersThree size={20} />
                    Gerenciar Agentes
                    <ArrowRight size={20} />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/shipper/quotes')}
                className="gap-2 text-white hover:bg-white/10"
              >
                <ArrowLeft size={18} />
                Voltar
              </Button>
              <div>
                <h1 className="text-2xl font-bold">
                  {isEditMode ? 'Editar Cotação' : 'Nova Cotação'}
                </h1>
                <p className="text-sm text-emerald-100">
                  {isEditMode ? 'Atualize os dados da sua cotação' : 'Preencha os dados para solicitar cotação'}
                </p>
              </div>
            </div>
            {!isEditMode && (
              <div>
                <input
                  ref={extractInputRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.docx,application/pdf,image/*"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && handleExtractDocument(e.target.files[0])}
                />
                <Button
                  type="button"
                  onClick={() => extractInputRef.current?.click()}
                  disabled={extracting}
                  className="gap-2 !bg-white !text-emerald-700 hover:!bg-emerald-50"
                >
                  <Sparkle size={18} weight="fill" />
                  {extracting ? 'Analisando documento...' : 'Preencher com documento'}
                  <span className="text-[10px] font-bold bg-emerald-600 text-white px-1.5 py-0.5 rounded">BETA</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Form Column */}
          <div className="flex-1">
            <form onSubmit={handleSubmit((d) => onSubmit(d, true))}>
              <Wizard steps={steps} currentStep={currentStep}>
            {/* Step 1: Modality Selection - Sub-step 1: Modal */}
            {currentStep === 0 && modalitySubStep === 'modal' && (
              <Card>
                <CardContent className="pt-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    Como sua carga será transportada?
                  </h2>
                  <p className="text-sm text-gray-600 mb-8">
                    Selecione o modal de transporte principal
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    {[
                      { id: 'maritimo' as const, icon: Boat, label: 'Marítimo' },
                      { id: 'aereo' as const, icon: Airplane, label: 'Aéreo' }
                    ].map((modal) => (
                      <button
                        key={modal.id}
                        type="button"
                        onClick={() => {
                          setSelectedModal(modal.id)
                          setModalitySubStep('details')
                          scrollToTop()
                        }}
                        className={`p-6 sm:p-8 border-2 rounded-xl transition-all hover:scale-105 hover:shadow-lg text-center
                          ${selectedModal === modal.id
                            ? 'border-emerald-500 bg-emerald-50'
                            : 'border-gray-200 hover:border-emerald-300'}`}
                      >
                        <modal.icon size={40} weight="duotone"
                          className={`sm:w-12 sm:h-12 ${selectedModal === modal.id ? 'text-emerald-600 mx-auto mb-3 sm:mb-4' : 'text-gray-400 mx-auto mb-3 sm:mb-4'}`} />
                        <span className="block font-bold text-base sm:text-lg text-gray-900">{modal.label}</span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 1: Modality Selection - Sub-step 2: Details */}
            {currentStep === 0 && modalitySubStep === 'details' && (
              <div className="space-y-6">
                <Card>
                  <CardContent className="pt-6">
                    <button
                      type="button"
                      onClick={() => {
                        setModalitySubStep('modal')
                        setSelectedType(null)
                        setSelectedVolume(null)
                        scrollToTop()
                      }}
                      className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
                    >
                      <ArrowLeft size={20} />
                      <span>Voltar</span>
                    </button>

                    <div className="flex items-center gap-3 mb-6">
                      {selectedModal === 'maritimo' && <Boat size={32} weight="duotone" className="text-emerald-600" />}
                      {selectedModal === 'aereo' && <Airplane size={32} weight="duotone" className="text-emerald-600" />}
                      {selectedModal === 'terrestre' && <Truck size={32} weight="duotone" className="text-emerald-600" />}
                      <h2 className="text-xl font-bold text-gray-900">
                        Transporte {selectedModal === 'maritimo' ? 'Maritimo' : selectedModal === 'aereo' ? 'Aereo' : 'Terrestre'}
                      </h2>
                    </div>

                    {/* Tipo de Operacao */}
                    <div className="mb-8">
                      <h3 className="font-semibold text-gray-700 mb-4">Tipo de Operacao</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[
                          { id: 'import' as const, label: 'Importacao', desc: 'Receber mercadorias do exterior' },
                          { id: 'export' as const, label: 'Exportacao', desc: 'Enviar mercadorias para o exterior' }
                        ].map((type) => (
                          <button
                            key={type.id}
                            type="button"
                            onClick={() => setSelectedType(type.id)}
                            className={`p-4 sm:p-6 border-2 rounded-xl transition-all text-left
                              ${selectedType === type.id
                                ? 'border-emerald-500 bg-emerald-50'
                                : 'border-gray-200 hover:border-emerald-300'}`}
                          >
                            <span className="block font-semibold text-gray-900">{type.label}</span>
                            <span className="text-xs sm:text-sm text-gray-500">{type.desc}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Tipo de Volume (apenas para Maritimo) */}
                    {selectedModal === 'maritimo' && (
                      <div className="mb-8">
                        <h3 className="font-semibold text-gray-700 mb-4">Tipo de Carga</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {[
                            { id: 'FCL' as const, label: 'FCL - Container Cheio', desc: 'Full Container Load' },
                            { id: 'LCL' as const, label: 'LCL - Carga Solta', desc: 'Less than Container Load' }
                          ].map((vol) => (
                            <button
                              key={vol.id}
                              type="button"
                              onClick={() => setSelectedVolume(vol.id)}
                              className={`p-4 sm:p-6 border-2 rounded-xl transition-all text-left
                                ${selectedVolume === vol.id
                                  ? 'border-emerald-500 bg-emerald-50'
                                  : 'border-gray-200 hover:border-emerald-300'}`}
                            >
                              <span className="block font-semibold text-gray-900">{vol.label}</span>
                              <span className="text-xs sm:text-sm text-gray-500">{vol.desc}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                  </CardContent>
                </Card>

                {/* Incoterm Card - aparece apos selecao completa */}
                {selectedType && (selectedModal !== 'maritimo' || selectedVolume) && (
                  <Card>
                    <CardContent className="pt-6">
                      <h2 className="text-xl font-bold text-gray-900 mb-2">Incoterm</h2>
                      <p className="text-sm text-gray-600 mb-4">Termo internacional de comercio</p>

                      <select
                        {...register('incoterm', { required: true })}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base"
                      >
                        <option value="">Selecione um incoterm...</option>
                        {Object.entries(INCOTERM_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>

                      <div className="mt-4">
                        <Label htmlFor="client_reference">Sua Referência (opcional)</Label>
                        <Input
                          id="client_reference"
                          {...register('client_reference')}
                          placeholder="Ex: PO-12345, Pedido #789..."
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Número de pedido, PO ou referência interna para seu controle
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Step 2: Route (Origin & Destination) */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                        <MapPin size={24} weight="duotone" className="text-emerald-600" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Origem</h2>
                        <p className="text-sm text-gray-600">De onde a carga será coletada</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <Controller
                        name="origin.location"
                        control={control}
                        render={({ field }) => (
                          <PortSelector
                            label={getOriginLocationType() ? 'Porto/Aeroporto de Origem' : 'Local de Coleta'}
                            type={getOriginLocationType()}
                            value={field.value}
                            onChange={(port) => {
                              setValue('origin.location.city', port.city)
                              setValue('origin.location.state', port.state)
                              setValue('origin.location.country', port.country)
                            }}
                            required
                            error={errors.origin?.location?.city?.message}
                          />
                        )}
                      />

                      <div>
                        <Label htmlFor="origin.collect_date">Data de Prontidão da Carga (Estimada)</Label>
                        <Input
                          type="date"
                          id="origin.collect_date"
                          {...register('origin.collect_date')}
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <MapPin size={24} weight="duotone" className="text-blue-600" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Destino</h2>
                        <p className="text-sm text-gray-600">Para onde a carga será enviada</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <Controller
                        name="destination.location"
                        control={control}
                        render={({ field }) => (
                          <PortSelector
                            label={getDestinationLocationType() ? 'Porto/Aeroporto de Destino' : 'Local de Entrega'}
                            type={getDestinationLocationType()}
                            value={field.value}
                            onChange={(port) => {
                              setValue('destination.location.city', port.city)
                              setValue('destination.location.state', port.state)
                              setValue('destination.location.country', port.country)
                            }}
                            required
                            error={errors.destination?.location?.city?.message}
                          />
                        )}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Data Limite para Receber Propostas */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                        <CalendarBlank size={24} weight="duotone" className="text-purple-600" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Data Limite</h2>
                        <p className="text-sm text-gray-600">Prazo para receber propostas (opcional)</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="quote_deadline">Data Limite para Propostas</Label>
                        <Input
                          type="date"
                          id="quote_deadline"
                          {...register('quote_deadline')}
                          min={new Date().toISOString().split('T')[0]}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Após esta data, agentes de carga não poderão mais enviar propostas
                        </p>
                      </div>

                    </div>
                  </CardContent>
                </Card>

                {/* Third Location - for EXW, DAP, DPU, DDP */}
                {needsThirdLocation().needed && (
                  <Card className="border-2 border-amber-200 bg-amber-50/30">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                          <MapPin size={24} weight="duotone" className="text-amber-600" />
                        </div>
                        <div>
                          <h2 className="text-xl font-bold text-gray-900">{needsThirdLocation().label}</h2>
                          <p className="text-sm text-gray-600">{needsThirdLocation().description}</p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <Controller
                          name="pickup_location.location"
                          control={control}
                          render={({ field }) => (
                            <AddressAutocomplete
                              label="Endereço Completo"
                              value={field.value}
                              onChange={(address) => {
                                setValue('pickup_location.location.city', address.city)
                                setValue('pickup_location.location.state', address.state)
                                setValue('pickup_location.location.country', address.country)
                                setValue('pickup_location.location.zip_code', address.zipCode)
                                // Store additional address details if needed
                                if (address.street) {
                                  setValue('pickup_location.location.street' as any, address.street)
                                }
                                if (address.number) {
                                  setValue('pickup_location.location.number' as any, address.number)
                                }
                              }}
                              required
                              error={errors.pickup_location?.location?.city?.message}
                            />
                          )}
                        />

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="pickup_location.contact_name">Nome do Contato *</Label>
                            <Input
                              id="pickup_location.contact_name"
                              {...register('pickup_location.contact_name', { required: needsThirdLocation().needed })}
                              placeholder="Nome do responsável"
                            />
                          </div>
                          <div>
                            <Controller
                              name="pickup_location.contact_phone"
                              control={control}
                              rules={{ required: needsThirdLocation().needed }}
                              render={({ field }) => (
                                <PhoneInput
                                  label="Telefone"
                                  value={field.value as any}
                                  onChange={(phone) => field.onChange(phone.fullNumber)}
                                  defaultCountry="BR"
                                  required
                                  error={errors.pickup_location?.contact_phone?.message}
                                />
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Step 3: Cargo Details */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <Card>
                  <CardContent className="pt-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Informações da Carga</h2>
                    <p className="text-sm text-gray-600 mb-6">Descreva o que será transportado</p>

                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="goods_description">Descrição da Carga *</Label>
                        <textarea
                          id="goods_description"
                          {...register('goods_description')}
                          rows={3}
                          className="mt-1 w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          placeholder="Ex: Equipamentos eletrônicos, Peças automotivas..."
                        />
                      </div>

                      {/* Multi-NCM input */}
                      <div>
                        <NCMAutocomplete
                          value=""
                          onSelect={(codigo) => {
                            if (codigo && !ncmList.includes(codigo)) {
                              const updated = [...ncmList, codigo]
                              setNcmList(updated)
                              setValue('goods_ncm_list', updated)
                              setValue('goods_ncm', updated[0])
                            }
                          }}
                          label="NCM (Nomenclatura Comum do Mercosul)"
                          placeholder="Digite o código ou descrição para adicionar"
                        />
                        {ncmList.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {ncmList.map((ncm, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-sm font-mono"
                              >
                                {ncm}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = ncmList.filter((_, i) => i !== idx)
                                    setNcmList(updated)
                                    setValue('goods_ncm_list', updated)
                                    setValue('goods_ncm', updated[0] || '')
                                  }}
                                  className="text-emerald-500 hover:text-red-500 transition-colors"
                                >
                                  <X size={14} weight="bold" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="goods_type">Tipo de Carga</Label>
                          <select
                            id="goods_type"
                            {...register('goods_type')}
                            className="mt-1 w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          >
                            <option value="">Selecione...</option>
                            {Object.entries(CARGO_TYPE_LABELS).map(([value, label]) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="flex items-end">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('goods_special')}
                              className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                            />
                            <span className="text-sm font-medium text-gray-700">Requer manuseio especial</span>
                          </label>
                        </div>
                      </div>

                      {/* Valor da mercadoria (base p/ seguro internacional e armazenagem) */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                        <div>
                          <Label htmlFor="goods_value">Valor da mercadoria</Label>
                          {/* Masked numeric input (Plane #31): thousands + 2 decimals,
                              pt-BR display (1.234.567,89). Stores the pure number. */}
                          <Controller
                            name="goods_value"
                            control={control}
                            render={({ field }) => (
                              <Input
                                id="goods_value"
                                type="text"
                                inputMode="decimal"
                                placeholder="0,00"
                                value={
                                  field.value != null && field.value !== ('' as any)
                                    ? Number(field.value).toLocaleString('pt-BR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })
                                    : ''
                                }
                                onChange={(e) => {
                                  const digits = e.target.value.replace(/\D/g, '')
                                  field.onChange(digits === '' ? null : Number(digits) / 100)
                                }}
                                className="mt-1"
                              />
                            )}
                          />
                          <p className="text-xs text-gray-500 mt-1">Base para cálculo de seguro internacional e armazenagem</p>
                        </div>
                        <div>
                          <Label htmlFor="goods_value_currency">Moeda</Label>
                          <select
                            id="goods_value_currency"
                            {...register('goods_value_currency')}
                            className="mt-1 w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          >
                            <option value="USD">US$ USD</option>
                            <option value="BRL">R$ BRL</option>
                            <option value="EUR">€ EUR</option>
                            <option value="GBP">£ GBP</option>
                            <option value="CNY">¥ CNY</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">
                          {currentModality?.volume_name === 'FCL' ? 'Containers' : 'Volumes'}
                        </h2>
                        <p className="text-sm text-gray-600">
                          {currentModality?.volume_name === 'FCL'
                            ? 'Selecione os containers necessários'
                            : 'Adicione os volumes da sua carga'}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => append({
                          type: currentModality?.volume_name === 'FCL' ? 'Container Dry Box (20)' : 'Pallet',
                          quantity: 1,
                          weight: 0,
                          dimensions: currentModality?.volume_name === 'FCL'
                            ? undefined
                            : { length: 0, width: 0, height: 0, unit: 'cm' }
                        })}
                        className="gap-2"
                      >
                        <Plus size={16} />
                        {currentModality?.volume_name === 'FCL' ? 'Adicionar Container' : 'Adicionar Volume'}
                      </Button>
                    </div>

                    <div className="space-y-4">
                      {fields.map((field, index) => (
                        <div key={field.id} className="p-4 border-2 border-gray-200 rounded-lg hover:border-emerald-300 transition-colors">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="font-semibold text-gray-900">Volume {index + 1}</h3>
                            {fields.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => remove(index)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash size={16} />
                              </Button>
                            )}
                          </div>
                          {currentModality?.volume_name === 'FCL' ? (
                            // FCL: Container Selection
                            <div className="space-y-3">
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="sm:col-span-2 lg:col-span-1">
                                  <Label>Tipo de Container</Label>
                                  <select
                                    {...register(`volume_items.${index}.type` as const)}
                                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                  >
                                    <option value="Container Dry Box (20)">Container Dry Box (20)</option>
                                    <option value="Container Dry Box (40)">Container Dry Box (40)</option>
                                    <option value="Container Dry/HC (40)">Container Dry/HC (40)</option>
                                    <option value="Container Reefer (20)">Container Reefer (20)</option>
                                    <option value="Container Reefer (40)">Container Reefer (40)</option>
                                    <option value="Container Open Top (20)">Container Open Top (20)</option>
                                    <option value="Container Open Top (40)">Container Open Top (40)</option>
                                    <option value="Container Flat Rack (20)">Container Flat Rack (20)</option>
                                    <option value="Container Flat Rack (40)">Container Flat Rack (40)</option>
                                    <option value="Container Isotank (20)">Container Isotank (20)</option>
                                    <option value="Container NOR (40)">Container NOR (40)</option>
                                  </select>
                                </div>
                                <div>
                                  <Label>Quantidade</Label>
                                  <Input
                                    type="number"
                                    {...register(`volume_items.${index}.quantity` as const, { valueAsNumber: true })}
                                    min="1"
                                  />
                                </div>
                                <div className="min-w-[150px]">
                                  <Label className="whitespace-nowrap">Peso Bruto (kg)</Label>
                                  <Controller
                                    name={`volume_items.${index}.weight` as const}
                                    control={control}
                                    render={({ field }) => (
                                      <Input
                                        type="text"
                                        inputMode="decimal"
                                        placeholder="0,00"
                                        value={field.value ? Number(field.value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                                        onChange={(e) => {
                                          const digits = e.target.value.replace(/\D/g, '')
                                          field.onChange(digits === '' ? 0 : Number(digits) / 100)
                                        }}
                                      />
                                    )}
                                  />
                                </div>
                              </div>
                            </div>
                          ) : (
                            // LCL/Aereo: Individual Volumes with Dimensions
                            <div className="space-y-3">
                              {/* Row 1: Embalagem, Qtd, Peso */}
                              <div className="grid grid-cols-3 gap-3">
                                <div>
                                  <Label className="text-xs text-gray-600">Embalagem</Label>
                                  <select
                                    {...register(`volume_items.${index}.type` as const)}
                                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                                  >
                                    <option value="Pallet">Pallet</option>
                                    <option value="Caixa">Caixa</option>
                                    <option value="Sacaria Cafe">Sacaria Café</option>
                                    <option value="Sacaria Geral">Sacaria Geral</option>
                                    <option value="Bombona">Bombona</option>
                                    <option value="Granel">Granel</option>
                                    <option value="Fardo">Fardo</option>
                                    <option value="Rolo">Rolo</option>
                                    <option value="Bobina">Bobina</option>
                                  </select>
                                </div>
                                <div>
                                  <Label className="text-xs text-gray-600">Qtd</Label>
                                  <Input
                                    type="number"
                                    {...register(`volume_items.${index}.quantity` as const, { valueAsNumber: true })}
                                    min="1"
                                    className="text-sm"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs text-gray-600">Peso (kg)</Label>
                                  <Controller
                                    name={`volume_items.${index}.weight` as const}
                                    control={control}
                                    render={({ field }) => (
                                      <Input
                                        type="text"
                                        inputMode="decimal"
                                        placeholder="0,00"
                                        className="text-sm"
                                        value={field.value ? Number(field.value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                                        onChange={(e) => {
                                          const digits = e.target.value.replace(/\D/g, '')
                                          field.onChange(digits === '' ? 0 : Number(digits) / 100)
                                        }}
                                      />
                                    )}
                                  />
                                </div>
                              </div>
                              {/* Row 2: Dimensões C x L x A */}
                              <div className="grid grid-cols-4 gap-3">
                                <div>
                                  <Label className="text-xs text-gray-600">Comp. (C)</Label>
                                  <Input
                                    type="number"
                                    {...register(`volume_items.${index}.dimensions.length` as const, { valueAsNumber: true })}
                                    step="0.01"
                                    min="0"
                                    placeholder="0"
                                    className="text-sm"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs text-gray-600">Larg. (L)</Label>
                                  <Input
                                    type="number"
                                    {...register(`volume_items.${index}.dimensions.width` as const, { valueAsNumber: true })}
                                    step="0.01"
                                    min="0"
                                    placeholder="0"
                                    className="text-sm"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs text-gray-600">Alt. (A)</Label>
                                  <Input
                                    type="number"
                                    {...register(`volume_items.${index}.dimensions.height` as const, { valueAsNumber: true })}
                                    step="0.01"
                                    min="0"
                                    placeholder="0"
                                    className="text-sm"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs text-gray-600">Unid.</Label>
                                  <select
                                    {...register(`volume_items.${index}.dimensions.unit` as const)}
                                    className="w-full px-2 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                                  >
                                    <option value="cm">cm</option>
                                    <option value="m">m</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Summary Section for LCL/Aereo */}
                    {currentModality?.volume_name !== 'FCL' && fields.length > 0 && (
                      <div className="mt-6 p-4 bg-emerald-50 border-2 border-emerald-200 rounded-lg">
                        <h3 className="font-semibold text-emerald-900 mb-3">Totais</h3>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Volumes</p>
                            <p className="text-2xl font-bold text-emerald-700">
                              {(watch('volume_items') || []).reduce((sum, item) => sum + (item?.quantity || 0), 0)}
                            </p>
                          </div>
                          <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Peso Bruto Total</p>
                            <p className="text-2xl font-bold text-emerald-700">
                              {(() => {
                                const items = watch('volume_items') || []
                                const totalKg = items.reduce((sum, item) => {
                                  if (!item?.weight) return sum
                                  return sum + (item.weight * (item.quantity || 1))
                                }, 0)
                                return `${totalKg.toFixed(2)} kg`
                              })()}
                            </p>
                          </div>
                          <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Cubagem Total</p>
                            <p className="text-2xl font-bold text-emerald-700">
                              {(() => {
                                const items = watch('volume_items') || []
                                const totalM3 = items.reduce((sum, item) => {
                                  if (!item?.dimensions) return sum
                                  const { length = 0, width = 0, height = 0, unit = 'cm' } = item.dimensions
                                  // Convert to meters
                                  const factor = unit === 'cm' ? 0.01 : 1
                                  const volumeM3 = (length * factor) * (width * factor) * (height * factor)
                                  return sum + (volumeM3 * (item.quantity || 1))
                                }, 0)
                                return `${totalM3.toFixed(3)} m³`
                              })()}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Step 4: Review & Submit */}
            {currentStep === 3 && (
              <Card>
                <CardContent className="pt-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Revise sua Cotação</h2>
                  <p className="text-sm text-gray-600 mb-6">Confirme todas as informações antes de enviar</p>

                  <div className="space-y-6">
                    {/* Modality Summary */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-semibold text-gray-900 mb-3">Modalidade e Termos</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Modalidade:</span>
                          <p className="font-medium">{currentModality?.name.toUpperCase()}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Incoterm:</span>
                          <p className="font-medium">{watch('incoterm')}</p>
                        </div>
                        {watch('client_reference') && (
                          <div>
                            <span className="text-gray-600">Referência:</span>
                            <p className="font-medium">{watch('client_reference')}</p>
                          </div>
                        )}
                        {watch('quote_deadline') && (
                          <div className="col-span-2">
                            <span className="text-gray-600">Prazo para Propostas:</span>
                            <p className="font-medium">
                              {formatDateBR(watch('quote_deadline'))}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Route Summary */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-semibold text-gray-900 mb-3">Rota</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Origem:</span>
                          <p className="font-medium">{watch('origin.location.city')}, {watch('origin.location.country')}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Destino:</span>
                          <p className="font-medium">{watch('destination.location.city')}, {watch('destination.location.country')}</p>
                        </div>
                      </div>
                    </div>

                    {/* Cargo Summary */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-semibold text-gray-900 mb-3">Carga</h3>
                      <p className="text-sm text-gray-600 mb-2">{watch('goods_description')}</p>
                      <p className="text-sm">
                        <span className="text-gray-600">Volumes:</span>{' '}
                        <span className="font-medium">{watch('volume_items')?.length} volume(s)</span>
                      </p>
                    </div>

                    {/* Notes / Observações */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-semibold text-gray-900 mb-1">Observações (opcional)</h3>
                      <p className="text-xs text-gray-500 mb-3">
                        Informações extras para os agentes — prazos, exigências, contexto da operação, etc.
                      </p>
                      <textarea
                        {...register('notes')}
                        rows={4}
                        placeholder="Ex: Carga precisa de embarque até o fim do mês; preferência por rota direta..."
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-y"
                      />
                    </div>

                    {/* Documents */}
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h3 className="font-semibold text-gray-900 mb-1">Documentos (opcional)</h3>
                      <p className="text-xs text-gray-500 mb-3">
                        Anexe invoice, packing list, fotos da carga, etc. Os agentes poderão visualizá-los ao cotar.
                      </p>
                      <DocumentAttachments
                        documents={attachedDocs}
                        onChange={setAttachedDocs}
                        uploader={uploadQuoteDocument}
                      />
                    </div>

                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <p className="text-sm text-emerald-900">
                        ✅ Sua cotação será enviada para os agentes de carga. Você receberá propostas em breve!
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Wizard Actions */}
            <WizardActions
              onBack={handleBack}
              onNext={handleNext}
              onSubmit={handleSubmit((d) => onSubmit(d, true))}
              onSaveDraft={isEditMode ? undefined : handleSubmit((d) => onSubmit(d, false))}
              isFirstStep={currentStep === 0}
              isLastStep={currentStep === steps.length - 1}
              isSubmitting={loading}
              canProgress={Boolean(canProgress())}
            />
          </Wizard>
        </form>
      </div>

      {/* Summary Sidebar */}
      <div className="hidden lg:block">
        <QuoteSummary
          watch={watch}
          currentStep={currentStep}
          modalities={modalities}
        />
      </div>
    </div>
  </main>

  {/* BETA: review what the AI captured before pre-filling the wizard */}
  {reviewData && (() => {
    const ex = reviewData.extracted || {}
    const modalityLabel: Record<string, string> = { maritimo: 'Marítimo', aereo: 'Aéreo', terrestre: 'Terrestre' }
    const opLabel: Record<string, string> = { import: 'Importação', export: 'Exportação' }
    const join = (a?: string | null, b?: string | null) => [a, b].filter(Boolean).join(', ')
    const rows: { label: string; value: string | null }[] = [
      { label: 'Modalidade', value: ex.modality ? (modalityLabel[ex.modality] || ex.modality) : null },
      { label: 'Operação', value: ex.operation ? (opLabel[ex.operation] || ex.operation) : null },
      { label: 'Incoterm', value: ex.incoterm ? String(ex.incoterm).toUpperCase() : null },
      { label: 'Origem', value: join(ex.originCity, ex.originCountry) || null },
      { label: 'Destino', value: join(ex.destinationCity, ex.destinationCountry) || null },
      { label: 'Mercadoria', value: ex.goodsDescription || null },
      { label: 'NCM', value: ex.ncm || null },
      { label: 'Peso bruto (kg)', value: ex.grossWeightKg != null ? String(ex.grossWeightKg) : null },
      { label: 'Qtde de volumes', value: ex.volumesCount != null ? String(ex.volumesCount) : null },
      { label: 'Tipo de volume', value: ex.volumeType || null },
      { label: 'Referência (PO/Nota)', value: ex.clientReference || null },
    ]
    const captured = rows.filter(r => r.value)
    const missing = rows.filter(r => !r.value)
    return (
      <Dialog isOpen={true} onClose={() => setReviewData(null)} maxWidth="lg">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Sparkle size={20} weight="fill" className="text-emerald-600" />
            <h2 className="text-lg font-bold text-gray-900">Revise o que capturamos</h2>
            <span className="text-[10px] font-bold bg-emerald-600 text-white px-1.5 py-0.5 rounded">BETA</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {captured.length} de {rows.length} campos preenchidos. Confira e complete o resto no formulário.
          </p>
        </DialogHeader>
        <DialogContent className="max-h-[60vh] overflow-y-auto">
          <div className="divide-y divide-gray-100">
            {rows.map((r) => (
              <div key={r.label} className="flex items-start gap-3 py-2.5">
                {r.value
                  ? <CheckCircle size={18} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" />
                  : <WarningCircle size={18} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />}
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-gray-500">{r.label}</div>
                  <div className={`text-sm ${r.value ? 'text-gray-900' : 'text-amber-600 italic'}`}>
                    {r.value || 'não capturado — preencha manualmente'}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {missing.length > 0 && (
            <p className="mt-3 text-xs text-gray-400">
              {missing.length} campo(s) não encontrado(s) no documento. Registramos isso (anônimo) para melhorar a extração.
            </p>
          )}
        </DialogContent>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setReviewData(null)}>
            Cancelar
          </Button>
          <Button
            type="button"
            className="!bg-emerald-600 !text-white hover:!bg-emerald-700"
            onClick={() => {
              logExtractionFeedback(ex, missing.map(m => m.label))
              applyExtracted(reviewData.extracted, reviewData.modalityId)
            }}
          >
            Confirmar e preencher
          </Button>
        </DialogFooter>
      </Dialog>
    )
  })()}
  </AppLayout>
  )
}
